import clsxm from '@/lib/clsxm';

import { IconColor, IconProps } from '@/components/icons/base-icon';
import {
  IconFeature1stToApply,
  IconFeature1stToApplyFilled,
  IconFeatureAutoOffer,
  IconFeatureAutoOfferFilled,
  IconFeatureEasyApply,
  IconFeatureEasyApplyFilled,
  IconFeatureExclusive,
  IconFeatureExclusiveFilled,
  IconFeatureNearby,
  IconFeatureNearbyFilled,
  IconFeatureNewItem,
  IconFeatureNewItemFilled,
  IconFeatureOneClick,
  IconFeatureOneClickFilled,
  IconFeaturePayRate,
  IconFeaturePayRateFilled,
  IconFeatureSocialProof,
  IconFeatureSocialProofFilled,
  IconFeatureTopHospital,
  IconFeatureTopHospitalFilled,
  IconFeatureTopMatch,
  IconFeatureTopMatchFilled,
  IconStatusSuccess,
} from '@/components/icons/nomad-ui-icons';
import Typography from '@/components/typography';

import {
  JobTagDynamicName,
  JobTagFormat,
  JobTagName,
} from '@/services/public-jobs.service';

export type JobTagDesign = 'primary' | 'card-corner-right';

export type JobTagProps = {
  tag: JobTagFormat;
  className?: string;
  showIcon?: boolean;
  design?: JobTagDesign;
};

export type TagTitleMappingFunction = (
  value?: string | number
) => undefined | string;

type TagStringMap = Record<Exclude<JobTagName, JobTagDynamicName>, string>;
type TagFunctionMap = Record<
  Extract<JobTagName, JobTagDynamicName>,
  TagTitleMappingFunction
>;

export const tagsDesignMap: Record<JobTagDesign, string> = {
  primary: 'rounded-full',
  'card-corner-right': 'rounded-bl-xl rounded-tr-xl',
};

export const tagsBackgroundColorMap: Record<JobTagDesign, string> = {
  primary: '!bg-light-purple',
  'card-corner-right': '!bg-light-blue',
};

export const tagsIconColorMap: Record<JobTagDesign, IconColor> = {
  primary: IconColor.ULTRAVIOLET,
  'card-corner-right': IconColor.INDIGO,
};

export const tagsTitleMap: TagStringMap & TagFunctionMap = {
  aboveAvgPay: 'Above avg. rate',
  applicationSubmitted: 'Application submitted',
  autoOffer: 'Auto-offer',
  easyApply: 'Easy Apply',
  exclusive: 'Exclusive',
  featured: 'Featured',
  firstToApply: 'Be the 1st to apply',
  nearby: 'Nearby',
  new: 'New',
  nomadsWorkedHere: (value?: string | number) =>
    `${value} Nomad${Number(value) > 1 ? 's' : ''} Worked Here` ?? value,
  oneClickApply: 'One-Click Apply',
  topHospital: 'Top Hospital',
  topMatch: 'Top Match',
};

export const tagHasIcon = (tag: JobTagName) =>
  !(tag in Object.keys(tagsFilledIconsMap));

// check https://github.com/NomadHealth/nomad-web-app/blob/master/features/fusion/components/tags/tagMapping.ts
export const tagsIconsMap: Partial<Record<JobTagName, React.FC<IconProps>>> = {
  aboveAvgPay: IconFeaturePayRate,
  applicationSubmitted: IconStatusSuccess,
  autoOffer: IconFeatureAutoOffer,
  easyApply: IconFeatureEasyApply,
  exclusive: IconFeatureExclusive,
  firstToApply: IconFeature1stToApply,
  nearby: IconFeatureNearby,
  new: IconFeatureNewItem,
  nomadsWorkedHere: IconFeatureSocialProof,
  oneClickApply: IconFeatureOneClick,
  topHospital: IconFeatureTopHospital,
  topMatch: IconFeatureTopMatch,
};

export const tagsFilledIconsMap: Partial<
  Record<JobTagName, React.FC<IconProps>>
> = {
  aboveAvgPay: IconFeaturePayRateFilled,
  applicationSubmitted: IconStatusSuccess,
  autoOffer: IconFeatureAutoOfferFilled,
  easyApply: IconFeatureEasyApplyFilled,
  exclusive: IconFeatureExclusiveFilled,
  firstToApply: IconFeature1stToApplyFilled,
  nearby: IconFeatureNearbyFilled,
  new: IconFeatureNewItemFilled,
  nomadsWorkedHere: IconFeatureSocialProofFilled,
  oneClickApply: IconFeatureOneClickFilled,
  topHospital: IconFeatureTopHospitalFilled,
  topMatch: IconFeatureTopMatchFilled,
};

export default function JobTag(props: JobTagProps) {
  const { className, design = 'primary', showIcon = true } = props;

  const tag = Array.isArray(props.tag) ? props.tag[0] : props.tag;
  const value = Array.isArray(props.tag) ? props.tag[1] : undefined;
  const title: string | undefined =
    typeof tagsTitleMap[tag] === 'function'
      ? (tagsTitleMap[tag] as TagTitleMappingFunction)(value)
      : (tagsTitleMap[tag] as string);

  if (!title) return null;

  const Icon = showIcon && tag && tagHasIcon(tag) && tagsFilledIconsMap[tag];

  return (
    <Typography.Body35
      className={clsxm(
        'text-secondary flex h-8 w-fit min-w-fit items-center gap-1 px-2.5',
        tagsBackgroundColorMap[design],
        tagsDesignMap[design],
        className
      )}
    >
      <div className={clsxm(tagsIconColorMap[design])}>
        {Icon && <Icon color={tagsIconColorMap[design]} className='h-5' />}
      </div>
      {title}
    </Typography.Body35>
  );
}
